import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import LanguageSwitcher from '../common/LanguageSwitcher';
import ProfileMenu from './ProfileMenu';
import NavDropdown from '../common/NavDropdown';
import NavItem from '../common/NavItem';

export default function Navbar() {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();

  const isAuthPage = location.pathname === '/login' || 
                    location.pathname === '/register' || 
                    location.pathname.startsWith('/register/');

  useEffect(() => {
    async function checkAdminStatus() {
      if (!currentUser) return;
      
      const systemAdminsDoc = await getDoc(doc(db, 'systemSettings', 'system_admins'));
      if (systemAdminsDoc.exists()) {
        const { systemAdmins = [] } = systemAdminsDoc.data();
        setIsAdmin(systemAdmins.includes(currentUser.uid));
      }
    }

    checkAdminStatus();
  }, [currentUser]);

  const userMenuItems = [
    { to: '/dashboard', icon: Icons.Home, label: t('nav.dashboard') },
    //{ to: '/investments', icon: Icons.PiggyBank, label: t('nav.investments') },
    //{ to: '/tickets', icon: Icons.TicketIcon, label: t('nav.support') }
  ];

  return isAuthPage ? null : (
    <nav className="bg-white shadow-sm">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="text-xl font-bold text-indigo-600">
                <div className="bg-indigo-600 p-2 rounded">
                <svg width="162" height="30" viewBox="0 0 324 59" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M0.3025 47V5.784H33.2625V16.92H15.7905V20.632H30.8305V32.152H15.7905V35.864H33.2625V47H0.3025ZM47.9625 47.64C43.4825 47.64 40.3252 46.744 38.4905 44.952C36.6985 43.16 35.8025 40.3867 35.8025 36.632V3.864H51.2905V34.584C51.2905 35.3947 51.4398 35.992 51.7385 36.376C52.0798 36.76 52.6772 36.952 53.5305 36.952C54.7252 36.952 55.7918 36.76 56.7305 36.376V45.976C55.4078 46.5307 54.1492 46.936 52.9545 47.192C51.7598 47.4907 50.0958 47.64 47.9625 47.64ZM60.6465 13.592L66.7265 0.599998H79.9105L72.6145 13.592H60.6465ZM77.9265 13.592L84.0065 0.599998H97.1905L89.8945 13.592H77.9265ZM77.0945 47.64C70.6518 47.64 65.8518 46.3173 62.6945 43.672C59.5798 41.0267 58.0225 37.08 58.0225 31.832C58.0225 26.584 59.5798 22.6373 62.6945 19.992C65.8518 17.3467 70.6518 16.024 77.0945 16.024C89.8518 16.024 96.2305 21.2933 96.2305 31.832C96.2305 42.3707 89.8518 47.64 77.0945 47.64ZM77.0945 36.568C78.2038 36.568 79.0358 36.2267 79.5905 35.544C80.1452 34.8613 80.4225 33.624 80.4225 31.832C80.4225 30.04 80.1452 28.8027 79.5905 28.12C79.0358 27.4373 78.2038 27.096 77.0945 27.096C76.0278 27.096 75.2172 27.4373 74.6625 28.12C74.1078 28.8027 73.8305 30.04 73.8305 31.832C73.8305 33.624 74.1078 34.8613 74.6625 35.544C75.2172 36.2267 76.0278 36.568 77.0945 36.568ZM99.4275 47V16.664H113.956L114.276 18.392C115.385 17.7947 116.878 17.2613 118.756 16.792C120.676 16.28 122.745 16.024 124.964 16.024C128.59 16.024 131.214 16.984 132.836 18.904C134.5 20.7813 135.332 23.64 135.332 27.48V47H119.844V29.464C119.844 28.0987 119.14 27.416 117.732 27.416C117.305 27.416 116.793 27.544 116.196 27.8C115.641 28.056 115.214 28.3547 114.916 28.696V47H99.4275ZM150.281 58.264C148.19 58.264 146.484 58.1573 145.161 57.944C143.881 57.7307 142.58 57.3467 141.257 56.792V46.936C142.494 47.3627 143.774 47.576 145.097 47.576C146.377 47.576 147.444 47.4693 148.297 47.256C149.15 47.0853 149.876 46.68 150.473 46.04L136.585 16.664H152.777L158.089 31.768L163.145 16.664H179.273L168.841 43.352C167.006 48.0027 164.617 51.6507 161.673 54.296C158.729 56.9413 154.932 58.264 150.281 58.264ZM181.178 47V5.784H196.666V17.304L205.69 5.784H223.162L209.082 22.872L224.442 47H208.058L199.61 32.664L196.666 36.248V47H181.178ZM237.213 47.64C232.733 47.64 229.575 46.744 227.741 44.952C225.949 43.16 225.053 40.3867 225.053 36.632V3.864H240.541V34.584C240.541 35.3947 240.69 35.992 240.989 36.376C241.33 36.76 241.927 36.952 242.781 36.952C243.975 36.952 245.042 36.76 245.981 36.376V45.976C244.658 46.5307 243.399 46.936 242.205 47.192C241.01 47.4907 239.346 47.64 237.213 47.64ZM257.744 47.64C254.33 47.64 251.621 46.6373 249.616 44.632C247.61 42.6267 246.608 39.704 246.608 35.864V16.664H262.096V34.2C262.096 35.5653 262.8 36.248 264.208 36.248C264.677 36.248 265.189 36.12 265.744 35.864C266.298 35.608 266.725 35.3093 267.024 34.968V16.664H282.512V47H267.984L267.664 45.272C266.554 45.9547 265.125 46.5307 263.376 47C261.669 47.4267 259.792 47.64 257.744 47.64ZM309.646 47.64C307.811 47.64 306.211 47.4053 304.846 46.936C303.481 46.4667 302.243 45.8267 301.134 45.016L300.43 47H286.99V3.864H302.478V17.176C303.289 16.8347 304.313 16.5573 305.55 16.344C306.787 16.1307 307.961 16.024 309.07 16.024C313.763 16.024 317.411 17.1973 320.014 19.544C322.659 21.8907 323.982 25.9013 323.982 31.576C323.982 36.9947 322.659 41.0267 320.014 43.672C317.411 46.3173 313.955 47.64 309.646 47.64ZM305.166 36.568C306.105 36.568 306.83 36.248 307.342 35.608C307.897 34.9253 308.174 33.6667 308.174 31.832C308.174 29.9973 307.897 28.76 307.342 28.12C306.83 27.4373 306.105 27.096 305.166 27.096C304.142 27.096 303.246 27.416 302.478 28.056V35.608C302.905 35.9067 303.31 36.1413 303.694 36.312C304.121 36.4827 304.611 36.568 305.166 36.568Z" fill="white"/>
</svg>
                </div>
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-4">
              {!isAdmin && userMenuItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-900 hover:text-indigo-600"
                >
                  <item.icon className="h-5 w-5 mr-2" />
                  {item.label}
                </Link>
              ))}
              {isAdmin && (
                <>
                  <NavItem to="/admin/dashboard" icon="LayoutDashboard" text={t('navbar.menu.dashboard')} />
                  <NavDropdown
                    id="projects"
                    icon="FolderGit2"
                    title={t('navbar.menu.projects.title')}
                    items={[
                      { 
                        to: "/admin/projects", 
                        icon: "Files", 
                        text: t('navbar.menu.projects.all') 
                      },
                      { 
                        to: "/admin/projects/archived", 
                        icon: "Archive", 
                        text: t('navbar.menu.projects.archived') 
                      },
                      { 
                        to: "/admin/projects/deleted", 
                        icon: "Trash2", 
                        text: t('navbar.menu.projects.deleted') 
                      }
                    ]}
                  />
                  <NavDropdown
                    id="customers"
                    icon="Users" 
                    title={t('navbar.menu.customers.title')}
                    items={[
                      { 
                        to: "/admin/customers", 
                        icon: "Users", 
                        text: t('navbar.menu.customers.all')
                      },
                      { 
                        to: "/admin/user-groups",
                        icon: "UsersRound", 
                        text: t('navbar.menu.customers.groups')
                      },
                      { 
                        to: "/admin/email", 
                        icon: "Mail", 
                        text: t('navbar.menu.customers.email')
                      },
                      { 
                        to: "/admin/sendnotifications", 
                        icon: "Send", 
                        text: t('navbar.menu.customers.notifications')
                      },
                      { 
                        to: "/admin/referrals", 
                        icon: "GitBranch", 
                        text: t('navbar.menu.customers.referrals') 
                      },
                      { 
                        to: "/admin/referrals/tree", 
                        icon: "Network", 
                        text: t('navbar.menu.customers.referralTree') 
                      }
                    ]}
                  />
                  {/*
                  <NavDropdown
                    id="tickets"
                    icon="TicketIcon"
                    title={t('tickets.title')}
                    items={[
                      { 
                        to: "/admin/tickets", 
                        icon: "Ticket", 
                        text: t('tickets.table.all')
                      }
                    ]}
                  />
                  <NavDropdown
                    id="plans"
                    icon="Album"
                    title={t('navbar.menu.plans.title')}
                    items={[
                      { to: "/admin/schedule", icon: "AlarmCheck", text: t('navbar.menu.plans.schedule') },
                      { to: "/admin/schema", icon: "Airplay", text: t('navbar.menu.plans.schema') }
                    ]}
                  />
                  <NavDropdown
                    id="transactions"
                    icon="Cast"
                    title={t('navbar.menu.transactions.title')}
                    items={[
                      { to: "/admin/transactions", icon: "Cast", text: t('navbar.menu.transactions.all') },
                      { to: "/admin/investments", icon: "Anchor", text: t('navbar.menu.transactions.investments') },
                      { to: "/admin/all-profits", icon: "CreditCard", text: t('navbar.menu.transactions.profits') }
                    ]}
                  />
                  <NavDropdown
                    id="gateways"
                    icon="DoorOpen"
                    title={t('navbar.menu.gateways.title')}
                    items={[
                      { to: "/admin/gateway/automatic", icon: "DoorOpen", text: t('navbar.menu.gateways.automatic') }
                    ]}
                  />
                  
                  <NavDropdown
                    id="deposits"
                    icon="ArrowDownCircle"
                    title={t('navbar.menu.deposits.title')}
                    items={[
                      { to: "/admin/deposit/method/list/auto", icon: "Workflow", text: t('navbar.menu.deposits.autoMethods') },
                      { to: "/admin/deposit/method/list/manual", icon: "Compass", text: t('navbar.menu.deposits.manualMethods') },
                      { to: "/admin/deposit/manual-pending", icon: "Columns", text: t('navbar.menu.deposits.pendingManual') },
                      { to: "/admin/deposit/history", icon: "ClipboardCheck", text: t('navbar.menu.deposits.history') }
                    ]}
                  />

*/}

                  <NavDropdown
                    id="discounts"
                    icon="Ticket"
                    title={t('navbar.menu.discounts.title')}
                    items={[
                      { 
                        to: "/admin/discounts", 
                        icon: "Percent",
                        text: t('navbar.menu.discounts.all')
                      },
                      { 
                        to: "/admin/discount-categories", 
                        icon: "FolderTree",
                        text: t('navbar.menu.discounts.categories')
                      }
                    ]}
                  />
                <NavDropdown
                    id="wallet"
                    icon="wallet"
                    title={t('navbar.menu.wallet.title')}
                    items={[
                      {
                        to: "/admin/wallet-users",
                        icon: "userSquare2",
                        text: t('navbar.menu.wallet.all')
                      }, 
                      {
                        to: "/admin/wallet-transactions",
                        icon: "arrowLeftRight",
                        text: t('navbar.menu.wallet.transactions')
                      },
                      {
                        to: "/admin/wallet-transfers",
                        icon: "arrowRightLeft",
                        text: t('navbar.menu.wallet.transfers')
                      },
                      {
                        to: "/admin/wallet-connections",
                        icon: "link",
                        text: "Wallet Kapcsolatok"
                      }
                    ]}
                  />
<NavDropdown
  id="system"
  icon="Settings"
  title={t('navbar.menu.system.title')}
  items={[
    {
      to: "/admin/logs",
      icon: "list",
      text: t('navbar.menu.system.logs')
    },
    {
      to: "/admin/email-templates", 
      icon: "Mail", 
      text: t('navbar.menu.system.emailTemplates')
    },
    { 
      to: "/admin/system-admins", 
      icon: "Shield", 
      text: t('navbar.menu.system.admins')
    }
  ]}
/>


     {/*
                  <NavDropdown
                    id="system"
                    icon="Settings"
                    title={t('navbar.menu.system.title')}
                    items={[
                      { 
                        to: "/admin/staff", 
                        icon: "UserCog", 
                        text: t('navbar.menu.staff.title')  // Staff Management
                      },
                      { 
                        to: "/admin/roles", 
                        icon: "Shield", 
                        text: t('navbar.menu.staff.roles')  // Manage Roles
                      },
                      { 
                        to: "/admin/pages", 
                        icon: "FileText", 
                        text: t('navbar.menu.system.pages') 
                      },
                      { 
                        to: "/admin/email-templates", 
                        icon: "Mail", 
                        text: t('navbar.menu.system.emailTemplates') 
                      },
                      { 
                        to: "/admin/sms-templates", 
                        icon: "MessageSquare", 
                        text: t('navbar.menu.system.smsTemplates') 
                      },
                      { 
                        to: "/admin/push-notifications", 
                        icon: "Bell", 
                        text: t('navbar.menu.system.pushNotifications') 
                      },
                      { 
                        to: "/admin/subscribers", 
                        icon: "Users", 
                        text: t('navbar.menu.system.subscribers') 
                      },
                      { 
                        to: "/admin/custom-css", 
                        icon: "Code", 
                        text: t('navbar.menu.system.customCss') 
                      },
                      { 
                        to: "/admin/clear-cache", 
                        icon: "RefreshCw", 
                        text: t('navbar.menu.system.clearCache') 
                      },
                      { 
                        to: "/admin/app-info", 
                        icon: "Info", 
                        text: t('navbar.menu.system.appInfo') 
                      },
                      { 
                        to: "/admin/landing-page", 
                        icon: "Layout", 
                        text: t('navbar.menu.system.landingPage') 
                      }
                    ]}
                  />*/}  
                </>
              )}
              
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            <ProfileMenu />
          </div>
        </div>
      </div>
    </nav>
  );
} 